import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ClipboardList, Layers, ChevronDown, LogOut, User, Settings } from 'lucide-react';
import { clientLogger } from '../utils/logger';
import { authenticatedFetch, fetchOrganizations } from '../utils/apiUtils';
import OrganizationManagement from './OrganizationManagement';

// Moved favicon logic to a regular function
const getFaviconUrl = (websiteUrl) => {
    if (!websiteUrl) return null;
    try {
        const url = new URL(websiteUrl);
        return `https://www.google.com/s2/favicons?domain=${url.hostname}`;
    } catch {
        return null;
    }
};

const OrganizationIcon = ({ websiteUrl, className = "w-4 h-4" }) => {
    const faviconUrl = getFaviconUrl(websiteUrl);
    
    if (!faviconUrl) return null;
    
    return (
        <img 
            src={faviconUrl}
            alt=""
            className={className}
            onError={(e) => e.target.style.display = 'none'}
        />
    );
};

const ProgressDonut = ({ progress, size = 16, strokeWidth = 2 }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    return (
        <div className="relative" style={{ width: size, height: size }}>
            {/* Background circle */}
            <svg className="absolute" width={size} height={size}>
                <circle
                    className="text-gray-200"
                    strokeWidth={strokeWidth}
                    stroke="currentColor"
                    fill="transparent"
                    r={radius}
                    cx={size / 2}
                    cy={size / 2}
                />
            </svg>
            {/* Progress circle */}
            <svg className="absolute transform -rotate-90" width={size} height={size}>
                <circle
                    className="text-green-600"
                    strokeWidth={strokeWidth}
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round"
                    stroke="currentColor"
                    fill="transparent"
                    r={radius}
                    cx={size / 2}
                    cy={size / 2}
                />
            </svg>
        </div>
    );
};

const Header = ({ user, currentOrganization, onLogout, onOrganizationChange, questionnaireProgress = 0, featureProgress = 0 }) => {
    const [organizations, setOrganizations] = useState([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showOrgManagement, setShowOrgManagement] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    // Helper function to determine if a route is active
    const isRouteActive = (path) => {
        if (path === '/requirements') {
            // Feature Selection is active when on /requirements and not in questionnaire view
            return location.pathname === path && !location.search.includes('showQuestionnaire=true');
        }
        if (path === '/requirements?showQuestionnaire=true') {
            // Requirements Questionnaire is active when showQuestionnaire=true
            return location.pathname === '/requirements' && location.search.includes('showQuestionnaire=true');
        }
        return location.pathname === path;
    };

    // Memoize organization data
    const organizationsWithIcons = useMemo(() => 
        organizations.map(org => ({
            ...org,
            faviconUrl: getFaviconUrl(org.website_url)
        })),
        [organizations]
    );

    // Fetch user's organizations
    useEffect(() => {
        if (user && user.id && !user.is_admin) {
            const loadOrganizations = async () => {
                try {
                    // Use our utility function to fetch organizations
                    const orgs = await fetchOrganizations();
                    setOrganizations(orgs);
                    
                    // Don't automatically set organization - let App.jsx handle this
                    // to prevent conflicts and infinite loops
                } catch (error) {
                    clientLogger.error('Error fetching organizations:', error);
                }
            };
            
            loadOrganizations();
        }
    }, [user, currentOrganization]);

    const handleOrganizationChange = async (org) => {
        if (!org?.id || org.id === currentOrganization?.id) {
            setIsDropdownOpen(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            console.log('Switching to organization:', org.id);
            
            const response = await authenticatedFetch(`/api/organizations/${org.id}/select`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json'
                }
            });

            // Check if response is JSON
            let data;
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                data = await response.json();
            }

            if (!response.ok) {
                throw new Error(data?.error || response.statusText || 'Failed to update organization selection');
            }

            if (!data?.success || !data?.organization) {
                throw new Error('Invalid server response');
            }

            // Update URL parameters
            const newUrl = new URL(window.location);
            newUrl.searchParams.set('org', org.id);
            window.history.pushState({}, '', newUrl);

            // Update App state
            onOrganizationChange(data.organization);
            setIsDropdownOpen(false);

            // Remove forced reload that was causing infinite loops
            // The React state update should be sufficient
        } catch (error) {
            console.error('Organization switch error:', error);
            setError(error.message || 'Failed to switch organization');
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogout = async () => {
        try {
            // Call the onLogout function provided by the parent component
            // which will handle the server-side logout request
            onLogout();
            
            // Also clear app demo state to prevent persistence between users
            localStorage.removeItem('appDemoState');
            
            // Clear saved material palettes to prevent accumulation across user sessions
            localStorage.removeItem('savedMaterialPalettes');
            
            // Navigate to the home page
            navigate('/');
        } catch (error) {
            console.error('Logout failed:', error);
            setError('Logout failed');
        }
    };

    return (
        <header className="bg-white shadow">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="flex justify-between items-center">
                    {/* Logo/Title */}
                    <h1 
                        onClick={() => navigate('/')}
                        className="text-xl font-semibold text-gray-900 cursor-pointer hover:text-green-600 transition-colors"
                    >
                        C360 Requirements
                    </h1>

                    {/* Main Navigation */}
                    <div className="flex items-center space-x-8">
                        {!user.is_admin && currentOrganization && (
                            <nav className="flex items-center space-x-6">
                                {/* Requirements Questionnaire Button */}
                                <button
                                    onClick={() => navigate('/requirements?showQuestionnaire=true')}
                                    className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors
                                        ${isRouteActive('/requirements?showQuestionnaire=true')
                                            ? 'text-green-600 bg-green-50' 
                                            : 'text-gray-700 hover:text-green-600 hover:bg-green-50'}`}
                                >
                                    <ClipboardList className="w-4 h-4 mr-2" />
                                    Requirements Questionnaire
                                    <div className="ml-2">
                                        <ProgressDonut progress={questionnaireProgress} />
                                    </div>
                                </button>

                                {/* Feature Selection Button */}
                                <button
                                    onClick={() => navigate('/requirements')}
                                    className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors
                                        ${isRouteActive('/requirements') 
                                            ? 'text-green-600 bg-green-50' 
                                            : 'text-gray-700 hover:text-green-600 hover:bg-green-50'}`}
                                >
                                    <Layers className="w-4 h-4 mr-2" />
                                    Feature Selection
                                    <div className="ml-2">
                                        <ProgressDonut progress={featureProgress} />
                                    </div>
                                </button>
                            </nav>
                        )}

                        {/* Right Section: Organization Selector and User Menu */}
                        <div className="flex items-center space-x-4 ml-8 border-l pl-8">
                            {/* Organization Selector */}
                            {!user.is_admin && currentOrganization && (
                                <div className="relative">
                                    <button
                                        onClick={() => !isLoading && setIsDropdownOpen(!isDropdownOpen)}
                                        disabled={isLoading}
                                        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                                    >
                                        <OrganizationIcon websiteUrl={currentOrganization.website_url} />
                                        <span>{currentOrganization.name}</span>
                                        <ChevronDown className="w-4 h-4 text-gray-500" />
                                        {isLoading && (
                                            <span className="ml-2 inline-block animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-green-600" />
                                        )}
                                    </button>

                                    {isDropdownOpen && organizationsWithIcons.length > 0 && (
                                        <div className="absolute right-0 mt-2 w-56 bg-white shadow-lg rounded-md overflow-hidden z-50">
                                            {organizationsWithIcons.map((org) => (
                                                <button
                                                    key={org.id}
                                                    onClick={() => handleOrganizationChange(org)}
                                                    disabled={isLoading}
                                                    className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-50
                                                        ${org.id === currentOrganization.id ? 'bg-green-50 text-green-700' : 'text-gray-700'}
                                                        ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <OrganizationIcon websiteUrl={org.website_url} />
                                                        {org.name}
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* User Menu */}
                            <div className="relative">
                                <button
                                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                                >
                                    <User className="w-4 h-4" />
                                    <span>{user.email}</span>
                                    <ChevronDown className="w-4 h-4 text-gray-500" />
                                </button>

                                {isUserMenuOpen && (
                                    <div className="absolute right-0 mt-2 w-48 bg-white shadow-lg rounded-md overflow-hidden z-50">
                                        {/* Admin links if user is admin */}
                                        {user?.is_admin && (
                                            <>
                                                <a href="/admin" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center">
                                                    <span className="mr-2">🛡️</span>
                                                    <span>Admin Dashboard</span>
                                                </a>
                                                <div className="border-t border-gray-100"></div>
                                            </>
                                        )}
                                        
                                        {/* Organization Management */}
                                        <button
                                            onClick={() => {
                                                setShowOrgManagement(true);
                                                setIsUserMenuOpen(false);
                                            }}
                                            className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                        >
                                            <Settings className="w-4 h-4" />
                                            Manage Organizations
                                        </button>
                                        
                                        {/* Domain processing link */}
                                        <a href="/manual-domain-queue" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center">
                                            <span className="mr-2">🌐</span>
                                            <span>Domain Processing</span>
                                        </a>
                                        
                                        {/* Logout button */}
                                        <button
                                            onClick={handleLogout}
                                            className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                        >
                                            <LogOut className="w-4 h-4" />
                                            Logout
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="absolute top-full mt-1 text-sm text-red-600 bg-white px-2 py-1 rounded shadow">
                        {error}
                    </div>
                )}
            </div>

            {/* Organization Management Modal */}
            {showOrgManagement && (
                <OrganizationManagement
                    user={user}
                    onClose={() => setShowOrgManagement(false)}
                />
            )}
        </header>
    );
};

export default Header;